function handleEvent(event, filters, handler) {
    for (let filter of filters) {
        if (!filter(event)) {
            return;
        }
    }
    if (handler) {
        handler(event);
    }
}
const keyCodes = {
    esc: 27,
    tab: 9,
    enter: 13,
    space: 32,
    up: 38,
    down: 40,
    del: [8, 46],
    left: 37,
    right: 39,
};
function createKeyFilter(keys) {
    const codes = [];
    for (const key of keys) {
        if (typeof key === 'number') {
            codes.push(key);
        }
        else {
            const code = keyCodes[key];
            if (typeof code === 'number') {
                codes.push(code);
            }
            else {
                codes.push(...code);
            }
        }
    }
    switch (codes.length) {
        case 0:
            return _ => false;
        case 1:
            const code = codes[0];
            return (e) => e.keyCode === code;
        default:
            return (e) => codes.indexOf(e.keyCode) >= 0;
    }
}
function defineChildModifier(target, currentFilters, name, filter, children) {
    Object.defineProperty(target, name, {
        get: function () {
            // call this getter at most once.
            // reuse created instance after next time.
            const ret = createModifier([...currentFilters, filter], children);
            Object.defineProperty(target, name, {
                value: ret,
                enumerable: true,
            });
            return ret;
        },
        enumerable: true,
        configurable: true,
    });
}
function defineKeyCodeModifiers(target, filters, children) {
    for (const name in keyCodes) {
        const keyName = name;
        if (keyName === 'left' || keyName === 'right') {
            continue;
        }
        const code = keyCodes[keyName];
        if (typeof code === 'number') {
            defineChildModifier(target, filters, keyName, (e) => e.keyCode === code, children);
        }
        else {
            const [c1, c2] = code;
            defineChildModifier(target, filters, keyName, (e) => e.keyCode === c1 || e.keyCode === c2, children);
        }
    }
}
function defineKeys(target, filters, children) {
    Object.defineProperty(target, 'keys', {
        get() {
            const keysFunction = (...args) => {
                const propName = 'keys:' + args.toString();
                const modifier = this[propName];
                if (modifier !== undefined) {
                    return modifier;
                }
                const filter = createKeyFilter(args);
                defineChildModifier(this, filters, propName, filter, children);
                return this[propName];
            };
            Object.defineProperty(this, 'keys', {
                value: keysFunction,
                enumerable: true,
            });
            return keysFunction;
        },
        enumerable: true,
        configurable: true,
    });
}
function defineExact(target, filters, children) {
    Object.defineProperty(target, 'exact', {
        get() {
            const exactFunction = (...args) => {
                const propName = 'exact:' + args.toString();
                const modifier = this[propName];
                if (modifier !== undefined) {
                    return modifier;
                }
                const expected = {
                    ctrl: false,
                    shift: false,
                    alt: false,
                    meta: false,
                };
                args.forEach(arg => (expected[arg] = true));
                const filter = (e) => !!e.ctrlKey === expected.ctrl &&
                    !!e.shiftKey === expected.shift &&
                    !!e.altKey === expected.alt &&
                    !!e.metaKey === expected.meta;
                defineChildModifier(this, filters, propName, filter, children);
                return this[propName];
            };
            Object.defineProperty(this, 'exact', {
                value: exactFunction,
                enumerable: true,
            });
            return exactFunction;
        },
        enumerable: true,
        configurable: true,
    });
}
function createModifier(filters, children) {
    function m(arg) {
        if (arg instanceof Function) {
            // EventHandler => EventHandler
            return (event) => handleEvent(event, filters, arg);
        }
        else {
            // Event => void
            handleEvent(arg, filters);
            return;
        }
    }
    if (children.keyboard || children.mouse) {
        const nextChildren = { ...children, keyboard: false, mouse: false };
        if (children.keyboard) {
            defineKeyCodeModifiers(m, filters, nextChildren);
            defineKeys(m, filters, nextChildren);
        }
        if (children.mouse) {
            defineChildModifier(m, filters, 'middle', (e) => e.button === 1, nextChildren);
        }
        defineChildModifier(m, filters, 'left', (e) => e.keyCode === 37 || e.button === 0, nextChildren);
        defineChildModifier(m, filters, 'right', (e) => e.keyCode === 39 || e.button === 2, nextChildren);
    }
    if (children.exact) {
        const nextChildren = { ...children, exact: false, modkey: false };
        defineExact(m, filters, nextChildren);
    }
    if (children.modkey) {
        const nextChildren = { ...children, exact: false };
        defineChildModifier(m, filters, 'ctrl', (e) => e.ctrlKey, nextChildren);
        defineChildModifier(m, filters, 'shift', (e) => e.shiftKey, nextChildren);
        defineChildModifier(m, filters, 'alt', (e) => e.altKey, nextChildren);
        defineChildModifier(m, filters, 'meta', (e) => e.metaKey, nextChildren);
        defineChildModifier(m, filters, 'noctrl', (e) => !e.ctrlKey, nextChildren);
        defineChildModifier(m, filters, 'noshift', (e) => !e.shiftKey, nextChildren);
        defineChildModifier(m, filters, 'noalt', (e) => !e.altKey, nextChildren);
        defineChildModifier(m, filters, 'nometa', (e) => !e.metaKey, nextChildren);
    }
    defineChildModifier(m, filters, 'stop', e => {
        e.stopPropagation();
        return true;
    }, children);
    defineChildModifier(m, filters, 'prevent', e => {
        e.preventDefault();
        return true;
    }, children);
    defineChildModifier(m, filters, 'self', e => e.target === e.currentTarget, children);
    return m;
}
export const modifiers = createModifier([], {
    keyboard: true,
    mouse: true,
    modkey: true,
    exact: true,
});
